<script setup lang="ts">
import { computed, inject } from 'vue';
import { useRouter } from 'vue-router';
import { PEEK_ORIGIN_TITLE } from '@/composables/peekOrigin';
import { useOpenRecordInInspector } from '@/composables/useOpenRecordInInspector';
import { areaEntity, entityGate, recordPathFor } from '@/navigation/areas';
import { isGateOpen } from '@/navigation/gates';

/**
 * A related record's name, wherever it shows: a table cell, a card, the record
 * below a table, a peek, the full page.
 *
 * A plain click opens the record as a peek, so you keep your place. Inside a
 * peek it stacks another peek on top. On a phone the peek is a bottom sheet.
 * The name is still a real link to the record's page, so ⌘-click, Ctrl-click
 * and middle-click open the page in a new tab, as any link does.
 *
 * There used to be a small peek button beside the name, and the name itself
 * went to the full page. The name now does what the button did, so the button
 * is gone.
 *
 * A record type with no page of its own, or one the user cannot open, still
 * opens as a peek, but its name is not a link.
 */
const props = withDefaults(
    defineProps<{
        entityId: string;
        recordId: number | string;
        /** Read aloud: "Zone Front room". */
        label: string;
        /**
         * For a name inside running text or a truncating line. The wrapper is
         * plain inline rather than inline-flex: an inline-flex box cannot be
         * clipped by text-overflow, so a truncating line would drop the whole
         * name instead of ending it with "…".
         */
        inline?: boolean;
    }>(),
    { inline: false },
);

const { canOpen, openRecord } = useOpenRecordInInspector();
// The record this link sits inside, for the start of the peek's trail.
const originTitle = inject(PEEK_ORIGIN_TITLE, null);
const router = useRouter();

const recordPath = computed(() =>
    areaEntity(props.entityId) && isGateOpen(entityGate(props.entityId)) ? recordPathFor(props.entityId, props.recordId) : null,
);
const peekable = computed(() => canOpen(props.entityId));

function isPlainClick(event: MouseEvent): boolean {
    return event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
}

function peek() {
    if (originTitle?.value) {
        openRecord(props.entityId, props.recordId, { originTitle: originTitle.value });
    } else {
        openRecord(props.entityId, props.recordId);
    }
}

function onLinkClick(event: MouseEvent) {
    // The click is the link's, never the row's or the card's it sits in.
    event.stopPropagation();
    // A modified click is the browser's: a new tab or a new window.
    if (!isPlainClick(event)) return;
    event.preventDefault();
    if (peekable.value) {
        peek();
    } else if (recordPath.value) {
        void router.push(recordPath.value);
    }
}
</script>

<template>
  <!-- The wrapper takes the attributes a caller passes, such as its own test id,
       so the link inside keeps `record-link`. -->
  <span
    v-if="recordPath"
    :class="inline ? 'inline' : 'inline-flex min-w-0 max-w-full items-center align-baseline'"
  >
    <a
      :href="recordPath"
      class="underline-offset-2 hover:underline"
      :class="!inline && 'min-w-0 truncate'"
      data-testid="record-link"
      @click="onLinkClick"
    >
      <slot>{{ label }}</slot>
    </a>
  </span>
  <span
    v-else-if="peekable"
    role="button"
    tabindex="0"
    class="cursor-pointer underline-offset-2 hover:underline"
    data-testid="record-link-peek"
    :aria-label="`Open ${label} in a peek`"
    @click.stop="peek"
    @keydown.enter.stop.prevent="peek"
    @keydown.space.stop.prevent="peek"
  >
    <slot>{{ label }}</slot>
  </span>
  <span v-else><slot>{{ label }}</slot></span>
</template>
